import { FC, ReactNode, useState } from 'react'
import {
  CardContainer,
  CardHeader,
  CardBody,
  CardFooter,
  CardHeaderIcon,
  CardHeaderTitle,
  CardDatesRow,
  CardDateText,
  CardDateBold,
  DescriptionText
} from './reportCardV2.styles'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import CustomizeReportDialog from '../customize-report-dialog/CustomizeReportDialog'
import { PermissionTooltip } from '../../../../components/tooltip/PermissionTooltip'
import { OnyxiaTooltip } from 'ui/components/tooltip'
import FrameworkRemovedTooltip from '../framework-removed-tooltip/FrameworkRemovedTooltip'
import ThemeButton from 'ui/components/buttons/theme/Button'
import theme from 'theme'
import { ReportScheduleInfo, ValidateTemplateConfigResponse, ValidationErrorType } from 'models'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import ScheduleIndicator from '../schedule-indicator/ScheduleIndicator'
import { useAppSelector } from 'store/hooks'
import { selectThemeMode } from 'store/theme/themeSlice'

interface ReportCardV2Props {
  reportIcon?: ReactNode
  title?: string
  description?: string
  hasCustomizedSettings?: boolean
  isLoading?: boolean
  hasPermission?: boolean
  onDownload?: () => void
  onCustomize?: () => void
  onTemplateSaved?: (generatedTemplateId?: string) => void
  createdDate?: string | null
  lastEditedDate?: string | null
  lastGeneratedDate?: string | null
  headerActions?: ReactNode
  showSchedule?: boolean
  schedule?: ReportScheduleInfo | null
  onSchedule?: () => void
  canSchedule?: boolean
  validationResult?: ValidateTemplateConfigResponse
  isValidating?: boolean
  onFrameworkChange?: () => Promise<void>
}

const ReportCardV2: FC<ReportCardV2Props> = ({
  title,
  reportIcon,
  description,
  hasPermission,
  isLoading = false,
  hasCustomizedSettings = false,
  onDownload,
  onTemplateSaved,
  createdDate,
  lastEditedDate,
  lastGeneratedDate,
  headerActions,
  showSchedule = false,
  schedule,
  onSchedule,
  canSchedule = true,
  validationResult,
  isValidating = false,
  onFrameworkChange
}) => {
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const isLightTheme = useAppSelector(selectThemeMode) === 'light'

  const isInvalid = validationResult && !validationResult.valid
  const hasFrameworkError = validationResult?.errors?.some((e) => e.type === ValidationErrorType.FRAMEWORK)
  const isDownloadDisabled = isValidating || (validationResult !== undefined && !validationResult.valid)

  const customizeReport = () => {
    setIsOpenDialog(true)
  }

  const handleOnCloseDialog = () => {
    setIsOpenDialog(false)
  }

  return (
    <>
      <CardContainer>
        <CardHeader>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={3}>
              {reportIcon && (
                <Box sx={{ position: 'relative' }}>
                  <CardHeaderIcon>{reportIcon}</CardHeaderIcon>
                  {isInvalid ? (() => {
                    const invalidIcon = (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ExclamationCircleIcon
                          width={24}
                          height={24}
                          color={isLightTheme ? '#E68900' : theme.colorTokens.info.attention}
                        />
                      </Box>
                    )
                    return hasFrameworkError ? (
                      <FrameworkRemovedTooltip
                        message="The framework used for this report is no longer available. You can download this report using your current framework."
                        onFrameworkChange={onFrameworkChange}
                      >
                        {invalidIcon}
                      </FrameworkRemovedTooltip>
                    ) : (
                      <OnyxiaTooltip
                        arrow
                        placement="bottom-start"
                        title="This report includes categories or CPIs that are no longer available and may appear incomplete."
                      >
                        {invalidIcon}
                      </OnyxiaTooltip>
                    )
                  })() : schedule ? (
                    <ScheduleIndicator schedule={schedule} />
                  ) : null}
                </Box>
              )}
              <Stack spacing={0.5} justifyContent="center">
                <CardHeaderTitle>{title}</CardHeaderTitle>
                {lastGeneratedDate && (
                  <CardDateText>
                    Last generated: <CardDateBold>{lastGeneratedDate}</CardDateBold>
                  </CardDateText>
                )}
              </Stack>
            </Stack>
            {headerActions}
          </Stack>
        </CardHeader>
        <CardBody>
          <DescriptionText>{description}</DescriptionText>
          {(createdDate || lastEditedDate) && (
            <CardDatesRow>
              {createdDate && (
                <CardDateText>
                  Created: <CardDateBold>{createdDate}</CardDateBold>
                </CardDateText>
              )}
              {lastEditedDate && (
                <CardDateText>
                  Last edited: <CardDateBold>{lastEditedDate}</CardDateBold>
                </CardDateText>
              )}
            </CardDatesRow>
          )}
        </CardBody>
        <CardFooter>
          <Stack direction="row" spacing={1}>
            {showSchedule && (
              canSchedule ? (
                <ThemeButton
                  themevariant="secondary"
                  onClick={onSchedule}
                  sx={{ flex: 1, height: '48px' }}
                >
                  {schedule ? 'Edit Schedule' : 'Schedule'}
                </ThemeButton>
              ) : (
                <PermissionTooltip
                  PopperProps={{ sx: { marginTop: `-${theme.spacing.sm}px !important` } }}
                  title={
                    <Box className="tooltip-wrapper-permissions">
                      <Typography className="tooltip-text">Insufficient permissions</Typography>
                    </Box>
                  }
                >
                  <Box flex={1}>
                    <ThemeButton
                      themevariant="secondary"
                      disabled
                      disableTouchRipple
                      sx={{ width: '100%', height: '48px' }}
                    >
                      {schedule ? 'Edit Schedule' : 'Schedule'}
                    </ThemeButton>
                  </Box>
                </PermissionTooltip>
              )
            )}
            {hasCustomizedSettings && (
              <ThemeButton
                themevariant="secondary"
                onClick={customizeReport}
                sx={{ flex: 1, height: '48px' }}
              >
                Customize
              </ThemeButton>
            )}
            {hasPermission ? (
              <ThemeButton
                themevariant="tertiary"
                onClick={onDownload}
                disabled={isDownloadDisabled}
                sx={{ flex: 1, height: '48px', gap: `${theme.spacing.xSmall}px` }}
              >
                <>
                  {isLoading && <CircularProgress size={18} color="secondary" />}
                  Download
                </>
              </ThemeButton>
            ) : (
              <PermissionTooltip
                PopperProps={{
                  sx: {
                    marginTop: `-${theme.spacing.sm}px !important`
                  }
                }}
                title={
                  <>
                    <Box className="tooltip-wrapper-permissions">
                      <Typography className="tooltip-text">Insufficient permissions</Typography>
                    </Box>
                  </>
                }
              >
                <Box flex={1}>
                  <ThemeButton
                    themevariant="tertiary"
                    disabled
                    disableTouchRipple
                    sx={{ width: '100%', height: '48px' }}
                  >
                    Download
                  </ThemeButton>
                </Box>
              </PermissionTooltip>
            )}
          </Stack>
        </CardFooter>
      </CardContainer>
      <CustomizeReportDialog isOpen={isOpenDialog} onClose={handleOnCloseDialog} onTemplateSaved={onTemplateSaved} />
    </>
  )
}

export default ReportCardV2
